import type { WebsiteAnalysisSnapshot } from '@/features/website-analysis/api/types';
import type { OnboardingFormData } from './onboarding-form';

/**
 * Build the snapshot the writer expects from wizard form values + (optionally)
 * the analysis snapshot the wizard saw earlier (scanner response or operator
 * upload). Form values win for fields the wizard shows; the ref fills in the
 * ones the wizard has no UI for (packages, memberships, variants, siteTitle,
 * businessDescription).
 */
export function buildSnapshotFromForm(
  data: OnboardingFormData,
  ref: WebsiteAnalysisSnapshot | null,
  companyLogoUrl: string | undefined
): WebsiteAnalysisSnapshot {
  // Services: overlay per-service pricing/durations from `serviceDetails` on
  // top of whatever the ref knows about the service (which is where variants
  // live — the wizard has no UI to edit them per-service).
  const nameKey = (s: string) => s.trim().toLowerCase();
  const refServiceByName = new Map(
    (ref?.services ?? []).map((s) => [nameKey(s.name), s])
  );
  const services = (data.services ?? []).map((name) => {
    const existing = refServiceByName.get(nameKey(name));
    const details = data.serviceDetails?.[name];
    return {
      name,
      categoryName:
        data.serviceCategories?.[name]?.trim() ||
        existing?.categoryName ||
        'Uncategorised',
      priceType: details?.priceType ?? existing?.priceType,
      priceAmount:
        details?.priceType === 'free' || details?.priceType === 'poa'
          ? undefined
          : details?.priceCents != null
            ? details.priceCents / 100
            : existing?.priceAmount,
      pricingDescription: existing?.pricingDescription,
      // The description reaches the writer: the wizard holds the scanned one,
      // and the ref backs it up for a service the wizard never mapped.
      description: details?.description ?? existing?.description,
      // Variants: only kept when the ref had them AND the operator didn't
      // rename the service — a rename would orphan the variants. Default to
      // `[]` so the shape matches the schema's `.default([])` output type.
      variants: existing?.variants ?? [],
    };
  });

  // Practitioners: the wizard's practitioners field has `id` on each entry
  // (react-hook-form list key); the snapshot shape has no id.
  const practitioners = (data.practitioners ?? [])
    .filter((p) => p.name?.trim())
    .map((p) => ({
      name: p.name,
      title: p.title || undefined,
      email: p.email || undefined,
    }));

  const locations = (data.locations ?? []).map((loc) => ({
    name: loc.name || undefined,
    phone: loc.phone || undefined,
    addressLine1: loc.addressLine1,
    city: loc.city,
    county: loc.county || undefined,
    postalCode: loc.postalCode || undefined,
    country: loc.country,
    latitude: loc.latitude ?? undefined,
    longitude: loc.longitude ?? undefined,
  }));

  // The wire boundary is `snapshot: unknown` and the writer parses everything
  // through `websiteAnalysisSnapshotSchema` on the server, so a `present-but-
  // undefined` vs `optional-missing` mismatch here is a TypeScript-only
  // concern — the runtime shape is fine. Cast at the return rather than
  // conditionally spreading each of 15 fields.
  const snapshot: WebsiteAnalysisSnapshot = {
    // Start from the ref so packages/memberships/businessDescription/siteTitle
    // pass through untouched — the wizard has no UI to edit these.
    ...(ref ?? {}),
    businessName: data.companyName,
    services,
    practitioners,
    locations,
    businessHours: data.businessHours,
    // Prefer the uploaded logo (org's own S3) over the site's hotlink.
    logoUrl: companyLogoUrl ?? ref?.logoUrl,
    primaryColor: data.primaryColor,
    secondaryColor: data.secondaryColor,
    brandVoice: data.brandVoice,
    targetAudienceDescription: data.targetAudienceDescription,
    suggestedCredibilityLines: data.suggestedCredibilityLines,
    // The Step-3 URL fields are still populated the same way — either the
    // operator uploaded them in the snapshot, or the wizard collected them.
    facebookPageUrl: data.facebookPageUrl || undefined,
    bookingSystemUrl: data.bookingSystemUrl || undefined,
    bookingLink: data.bookingLink || undefined,
    bookingSystemChoice: data.bookingSystemChoice,
  } as WebsiteAnalysisSnapshot;
  return snapshot;
}
